import { useState, useEffect, useRef, useMemo } from 'react';
import { fmt, Spinner, SlipPreviewModal } from './UI';
import {
  getManagerDashboardData, subscribeToSubmissions,
  addSubmission, addDirectSubmission, uploadSlip,
  flagSubmissionForReview, clearSubmissionReview,
} from '../lib/supabase';
import PaymentSheet from './PaymentSheet';

const MN = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function addDays(dateStr, n) {
  const [y, mo, d] = dateStr.split('-').map(Number);
  const date = new Date(Date.UTC(y, mo - 1, d + n));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth()+1).padStart(2,'0')}-${String(date.getUTCDate()).padStart(2,'0')}`;
}

// Dates after last Excel date up to today (untracked dates for direct deposits)
function getMissingDates(transactions) {
  const dates = transactions.map(t => t.collection_date).filter(Boolean).sort();
  const maxDate = dates.length ? dates[dates.length - 1] : null;
  const start = maxDate ? addDays(maxDate, 1) : todayStr();
  const end   = todayStr();
  const result = [];
  for (let d = start; d <= end; d = addDays(d, 1)) result.push(d);
  return result;
}

function subMonth(s) {
  if (!s.txn_date) return null;
  const [, m,] = s.txn_date.split('-').map(Number);
  return `${MN[m - 1]}-${String(s.txn_date.split('-')[0]).slice(2)}`;
}

// History tab helpers (module-level — no state deps)
function histSubMonth(s) {
  const baseDate = s.txn_date || s.submission_date;
  if (!baseDate) return null;
  const [y, m] = baseDate.split('-');
  return `${MN[parseInt(m, 10) - 1]}-${String(y).slice(2)}`;
}
function histSubKind(s) {
  // Strip optional [ADMIN] prefix then trim before checking kind tag
  const core = (s.remarks || '').trim().replace(/^\[ADMIN\]\s*/i, '').trim();
  if (core.startsWith('[LOSS]'))   return 'Loss';
  if (core.startsWith('[SHORT]'))  return 'Short';
  if (core.startsWith('[EXCESS]')) return 'Excess';
  return 'Full';
}

const PAYMENT_MODES = [
  { key: 'cms',           label: '💵 Handover to CMS' },
  { key: 'neft_flipkart', label: '🏦 NEFT to Flipkart' },
  { key: 'neft_wheeley',  label: '🏦 NEFT to Wheeley'  },
];

// ── Submission Modal ───────────────────────────────────────────────────────────
// Used by the manager to submit payment details on behalf of a hub.
// txn = transaction object (for regular submissions) | null (for direct deposits)
// directDate = string date (for direct deposits), triggers when txn is null
function SubmitModal({ txn, uploadId, hub, directDate, defaultAmount, onClose, onSuccess, managerName }) {
  const isDirect = !txn;

  const [mode,           setMode]           = useState('cms');
  const [submissionKind, setSubmissionKind] = useState('full');
  const [form, setForm] = useState({
    amount:      defaultAmount > 0 ? String(defaultAmount) : '',
    ref:         '',
    date:        directDate || todayStr(),
    remarks:     '',
    submittedBy: `manager:${managerName}`,
  });
  const [slipFile,    setSlipFile]    = useState(null);
  const [slipPreview, setSlipPreview] = useState('');
  const [submitting,  setSubmitting]  = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const fileRef = useRef();

  function requestConfirm() {
    if (!form.date) return;
    if (submissionKind === 'loss') {
      if (!form.remarks.trim()) { alert('Remarks are required for a Loss/Utilized declaration — explain what happened to the money.'); return; }
      setShowConfirm(true); return;
    }
    if (!form.amount) return;
    if (!form.ref.trim()) { alert(mode === 'cms' ? 'CMS Ref / Slip No is required.' : 'UTR / Ref Number is required.'); return; }
    if (!slipFile) { alert('Please upload the deposit slip or screenshot.'); return; }
    if (submissionKind === 'short' && !form.remarks.trim()) { alert('Remarks are required for a short submission.'); return; }
    if (submissionKind === 'excess' && !form.remarks.trim()) { alert('Remarks required for excess — mention which date\'s shortfall this covers.'); return; }
    setShowConfirm(true);
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      let slipUrl = null;
      if (slipFile) slipUrl = await uploadSlip(slipFile, hub);

      const remarkTag =
        submissionKind === 'short'  ? '[SHORT]'  :
        submissionKind === 'excess' ? '[EXCESS]' :
        submissionKind === 'loss'   ? '[LOSS]'   : '[ADMIN]';
      const remarksStr = form.remarks.trim()
        ? `${remarkTag} ${form.remarks}`
        : submissionKind === 'full' ? `[ADMIN] submitted by ${managerName}` : remarkTag;

      const entry = {
        type:        submissionKind === 'loss' ? 'cms' : mode,
        amount:      parseFloat(form.amount) || 0,
        ref:         submissionKind === 'loss' ? '' : form.ref,
        date:        form.date,
        remarks:     remarksStr,
        submittedBy: form.submittedBy,
        slipUrl:     submissionKind === 'loss' ? null : slipUrl,
      };

      if (isDirect) {
        await addDirectSubmission(uploadId, hub, form.date, entry);
      } else {
        await addSubmission(uploadId, hub, txn.id, entry);
      }
      onSuccess();
    } catch (e) { alert('Submission failed: ' + e.message); }
    setSubmitting(false);
  }

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setSlipFile(file);
    const reader = new FileReader();
    reader.onload = ev => setSlipPreview(ev.target.result);
    reader.readAsDataURL(file);
  }

  const inp = {
    background: '#0b0e14', color: '#f3f4f6', border: '1px solid #374151',
    borderRadius: 8, width: '100%', padding: '9px 12px', fontSize: 13,
    fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000000cc', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#1a1f2e', border: '1px solid #2a2f3e', borderRadius: 16, width: '100%', maxWidth: 520, maxHeight: '92vh', overflowY: 'auto', fontFamily: "'DM Mono', monospace" }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #2a2f3e', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ color: '#f59e0b', fontWeight: 700, fontSize: 13 }}>
              {isDirect ? '＋ Direct Deposit' : '＋ Submit Payment'}
            </div>
            <div style={{ color: '#60a5fa', fontSize: 12, fontWeight: 700, marginTop: 3 }}>{hub}</div>
            {!isDirect && txn && (
              <div style={{ color: '#6b7280', fontSize: 11, marginTop: 1 }}>
                {txn.collection_date} · Total due ₹{fmt(txn.amount_to_collect)}
              </div>
            )}
            {isDirect && (
              <div style={{ color: '#6b7280', fontSize: 11, marginTop: 1 }}>Untracked / prior deposit</div>
            )}
            <div style={{ background: '#1c1a00', border: '1px solid #3d3000', borderRadius: 5, padding: '2px 8px', display: 'inline-block', marginTop: 5 }}>
              <span style={{ color: '#fbbf24', fontSize: 10, fontWeight: 700 }}>MANAGER ACTION</span>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', color: '#6b7280', border: 'none', fontSize: 20, cursor: 'pointer', padding: 4, lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Submission kind */}
          <div>
            <label style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 8 }}>SUBMISSION TYPE</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[
                { key: 'full',   label: 'Full'          },
                { key: 'short',  label: 'Short'         },
                { key: 'excess', label: 'Excess'        },
                { key: 'loss',   label: 'Loss/Utilized' },
              ].map(k => (
                <button key={k.key} onClick={() => setSubmissionKind(k.key)}
                  style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
                    fontWeight: submissionKind === k.key ? 700 : 400,
                    background: submissionKind === k.key ? '#f59e0b' : '#0f1117',
                    color:      submissionKind === k.key ? '#0f1117' : '#9ca3af',
                    border:     `1px solid ${submissionKind === k.key ? '#f59e0b' : '#374151'}` }}>
                  {k.label}
                </button>
              ))}
            </div>
          </div>

          {/* Payment mode */}
          {submissionKind !== 'loss' && (
            <div>
              <label style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 8 }}>PAYMENT MODE</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {PAYMENT_MODES.map(m => (
                  <button key={m.key} onClick={() => setMode(m.key)}
                    style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
                      fontWeight: mode === m.key ? 700 : 400,
                      background: mode === m.key ? '#1e3a5f' : '#0f1117',
                      color:      mode === m.key ? '#60a5fa' : '#6b7280',
                      border:     `1px solid ${mode === m.key ? '#3b82f6' : '#374151'}` }}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Amount */}
          {submissionKind !== 'loss' && (
            <div>
              <label style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 6 }}>AMOUNT (₹)</label>
              <input type="number" placeholder="Enter amount"
                value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                style={inp} />
            </div>
          )}

          {/* Date — always editable (especially important for direct deposits) */}
          <div>
            <label style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 6 }}>
              {isDirect ? 'COLLECTION DATE' : 'SUBMISSION DATE'}
            </label>
            <input type="date"
              value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
              style={inp} />
          </div>

          {/* Ref number */}
          {submissionKind !== 'loss' && (
            <div>
              <label style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 6 }}>
                {mode === 'cms' ? 'CMS REF / SLIP NO' : 'UTR / REF NUMBER'}
              </label>
              <input type="text" placeholder={mode === 'cms' ? 'CMS reference or slip number' : 'UTR / bank reference'}
                value={form.ref} onChange={e => setForm(f => ({ ...f, ref: e.target.value }))}
                style={inp} />
            </div>
          )}

          {/* Remarks */}
          <div>
            <label style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 6 }}>
              REMARKS {(submissionKind === 'loss' || submissionKind === 'short' || submissionKind === 'excess') ? '(REQUIRED)' : '(OPTIONAL)'}
            </label>
            <input type="text"
              placeholder={submissionKind === 'loss' ? 'Explain what happened to the money' : submissionKind === 'excess' ? 'Which date shortfall does this cover?' : 'Any notes'}
              value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))}
              style={inp} />
          </div>

          {/* Submitted By */}
          <div>
            <label style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 6 }}>SUBMITTED BY</label>
            <input type="text" placeholder="Name of person"
              value={form.submittedBy} onChange={e => setForm(f => ({ ...f, submittedBy: e.target.value }))}
              style={inp} />
          </div>

          {/* Deposit slip */}
          {submissionKind !== 'loss' && (
            <div>
              <label style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 6 }}>
                DEPOSIT SLIP (REQUIRED)
              </label>
              <div onClick={() => fileRef.current?.click()}
                style={{ background: '#0b0e14', border: `2px dashed ${slipFile ? '#34d399' : '#374151'}`, borderRadius: 8, padding: 16, textAlign: 'center', cursor: 'pointer', transition: 'border-color .2s' }}>
                {slipPreview
                  ? <img src={slipPreview} alt="Slip" style={{ maxWidth: '100%', maxHeight: 150, borderRadius: 6 }} />
                  : <div style={{ color: '#6b7280', fontSize: 12 }}>📎 Click to upload slip / screenshot</div>}
              </div>
              <input ref={fileRef} type="file" accept="image/*,.pdf" onChange={handleFile} style={{ display: 'none' }} />
              {slipFile && <div style={{ color: '#34d399', fontSize: 11, marginTop: 4 }}>✓ {slipFile.name}</div>}
            </div>
          )}

          {/* Confirm panel */}
          {showConfirm ? (
            <div style={{ background: '#0b0e14', border: '1px solid #f59e0b40', borderRadius: 10, padding: 16 }}>
              <div style={{ color: '#f59e0b', fontSize: 12, fontWeight: 700, marginBottom: 10 }}>Confirm Submission</div>
              <div style={{ color: '#9ca3af', fontSize: 12, lineHeight: 2 }}>
                <div>Hub: <span style={{ color: '#60a5fa', fontWeight: 700 }}>{hub}</span></div>
                {isDirect
                  ? <div>Collection Date: <span style={{ color: '#f3f4f6' }}>{form.date}</span></div>
                  : txn && <div>Transaction Date: <span style={{ color: '#f3f4f6' }}>{txn.collection_date}</span></div>}
                <div>Type: <span style={{ color: '#f3f4f6' }}>{submissionKind.toUpperCase()}</span>
                  {submissionKind !== 'loss' && <> via <span style={{ color: '#f3f4f6' }}>{mode.replace('_','-').toUpperCase()}</span></>}
                </div>
                {submissionKind !== 'loss' && <div>Amount: <span style={{ color: '#34d399', fontWeight: 700 }}>₹{fmt(parseFloat(form.amount) || 0)}</span></div>}
                {submissionKind !== 'loss' && form.ref && <div>Ref: <span style={{ color: '#f3f4f6' }}>#{form.ref}</span></div>}
                {form.remarks.trim() && <div>Remarks: <span style={{ color: '#f3f4f6', fontStyle: 'italic' }}>{form.remarks}</span></div>}
                <div>Submitted by: <span style={{ color: '#fbbf24' }}>{form.submittedBy}</span></div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                <button onClick={() => setShowConfirm(false)}
                  style={{ flex: 1, padding: 10, background: '#1a1f2e', color: '#9ca3af', border: '1px solid #374151', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12 }}>
                  ← Back
                </button>
                <button onClick={handleSubmit} disabled={submitting}
                  style={{ flex: 2, padding: 10, background: submitting ? '#2d1f00' : '#f59e0b', color: submitting ? '#6b7280' : '#0f1117', border: 'none', borderRadius: 8, fontWeight: 900, cursor: submitting ? 'wait' : 'pointer', fontFamily: 'inherit', fontSize: 12, letterSpacing: 1 }}>
                  {submitting ? 'SUBMITTING...' : '✓ CONFIRM'}
                </button>
              </div>
            </div>
          ) : (
            <button onClick={requestConfirm}
              style={{ width: '100%', padding: 12, background: '#f59e0b', color: '#0f1117', border: 'none', borderRadius: 8, fontWeight: 900, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, letterSpacing: 2 }}>
              REVIEW & SUBMIT →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────────────────
export default function ManagerDashboard({ user, onLogout }) {
  const managedHubs = user.managed_hubs || [];

  const [submissions,  setSubmissions]  = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [hubData,      setHubData]      = useState([]);
  const [uploadMeta,   setUploadMeta]   = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [expandedHub,  setExpandedHub]  = useState(null);
  const [summaryMonth, setSummaryMonth] = useState('All');
  const [search,       setSearch]       = useState('');
  const [submitModal,  setSubmitModal]  = useState(null); // { hub, txn, directDate, defaultAmount }

  const [section,      setSection]      = useState('payment'); // 'cod' | 'payment'
  // View tabs: 'summary' | 'history'
  const [managerView,  setManagerView]  = useState('summary');
  // Submission history filters
  const [histHub,      setHistHub]      = useState('All');
  const [histMonth,    setHistMonth]    = useState('All');
  const [histKind,     setHistKind]     = useState('All');
  const [histOpenOnly, setHistOpenOnly] = useState(false); // only show subs for open (non-closed) dates
  const [histDateFrom, setHistDateFrom] = useState('');
  const [histDateTo,   setHistDateTo]   = useState('');
  // Flag submission state
  const [flagSubId,    setFlagSubId]    = useState(null);
  const [flagNote,     setFlagNote]     = useState('');
  const [flagging,     setFlagging]     = useState(false);
  const [viewRemarks,  setViewRemarks]  = useState(null);
  const [previewSlip,  setPreviewSlip]  = useState(null);

  // Cell selection (Excel-style sum bar)
  const [selectedCells, setSelectedCells] = useState(new Map());
  function toggleCell(key, value) {
    setSelectedCells(prev => {
      const next = new Map(prev);
      if (next.has(key)) next.delete(key); else next.set(key, Number(value) || 0);
      return next;
    });
  }

  async function fetchData() {
    setLoading(true);
    try {
      const { submissions: s, transactions: t, hubData: h, uploadMeta: um } = await getManagerDashboardData(managedHubs);
      setSubmissions(s);
      setTransactions(t);
      setHubData(h);
      setUploadMeta(um);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  useEffect(() => {
    fetchData();
    const channel = subscribeToSubmissions(() => fetchData());
    return () => channel.unsubscribe();
  }, []); // eslint-disable-line

  // ── Derived maps ──────────────────────────────────────────────────────────────

  const subByHubDate = useMemo(() => {
    const m = {};
    const txnById = {};
    transactions.forEach(t => { txnById[t.id] = t; });
    submissions.forEach(s => {
      if (s.is_direct) return;
      const fallbackTxn = txnById[s.transaction_id];
      const effDate = s.txn_date || fallbackTxn?.collection_date;
      if (!effDate) return;
      const enriched = { ...s, txn_amount_to_collect: s.txn_amount_to_collect ?? fallbackTxn?.amount_to_collect ?? null };
      const key = `${enriched.hub}|${effDate}`;
      if (!m[key]) m[key] = [];
      m[key].push(enriched);
    });
    return m;
  }, [submissions, transactions]);

  const subByHubDateDirect = useMemo(() => {
    const m = {};
    submissions.forEach(s => {
      if (!s.is_direct || !s.txn_date) return;
      const key = `${s.hub}|${s.txn_date}`;
      if (!m[key]) m[key] = [];
      m[key].push(s);
    });
    return m;
  }, [submissions]);

  const subHubOrphan = useMemo(() => {
    const m = {};
    const txnById = {};
    transactions.forEach(t => { txnById[t.id] = t; });
    submissions.forEach(s => {
      if (s.is_direct) return;
      const effDate = s.txn_date || txnById[s.transaction_id]?.collection_date;
      if (effDate) return;
      if (!m[s.hub]) m[s.hub] = [];
      m[s.hub].push(s);
    });
    return m;
  }, [submissions, transactions]);

  const subMap = useMemo(() => {
    const m = {};
    submissions.forEach(s => {
      if (!m[s.hub]) m[s.hub] = [];
      m[s.hub].push(s);
    });
    return m;
  }, [submissions]);

  function computeSubmitted(hubTxnList) {
    const hub = hubTxnList[0]?.hub;
    if (!hub) return { submitted: 0, countedIds: new Set() };
    const uniqueDates = [...new Set(hubTxnList.map(t => t.collection_date))];
    const countedIds = new Set();
    let submitted = 0;
    uniqueDates.forEach(date => {
      (subByHubDate[`${hub}|${date}`] || []).forEach(s => {
        if (!countedIds.has(s.id)) { countedIds.add(s.id); submitted += Number(s.amount || 0); }
      });
    });
    (subHubOrphan[hub] || []).forEach(s => {
      if (s.is_direct) return;
      const matchesTxn = hubTxnList.some(t => t.amount_to_collect > 0 && Number(s.amount) === Number(t.amount_to_collect));
      if (matchesTxn && !countedIds.has(s.id)) { countedIds.add(s.id); submitted += Number(s.amount || 0); }
    });
    return { submitted, countedIds };
  }

  // ── Enriched hub rows ─────────────────────────────────────────────────────────

  const summaryMonths = useMemo(() => {
    const set = new Set();
    transactions.forEach(t => { if (t.month) set.add(t.month); });
    return ['All', ...Array.from(set).sort((a, b) => {
      const [am, ay] = a.split('-'); const [bm, by] = b.split('-');
      return (parseInt(by) - parseInt(ay)) || (MN.indexOf(bm) - MN.indexOf(am));
    })];
  }, [transactions]);

  const enriched = useMemo(() => {
    // Pre-compute hub|date keys where ALL rows are Closed → exclude from totals
    const CLOSED_LS_E = ['CLOSED', 'Closed', 'closed'];
    const _hubDateGroups = {};
    transactions.forEach(t => {
      const k = `${t.hub}|${t.collection_date}`;
      if (!_hubDateGroups[k]) _hubDateGroups[k] = [];
      _hubDateGroups[k].push(t);
    });
    const allClosedDateKeys = new Set(
      Object.entries(_hubDateGroups)
        .filter(([, g]) => g.every(t => CLOSED_LS_E.includes(t.line_status)))
        .map(([k]) => k)
    );
    const summaryTxns = transactions.filter(
      t => !allClosedDateKeys.has(`${t.hub}|${t.collection_date}`)
    );

    if (summaryMonth === 'All') {
      return hubData.map(h => {
        const subs       = subMap[h.hub] || [];
        const directSubs = subs.filter(s => s.is_direct);
        const hubTxns    = summaryTxns.filter(t => t.hub === h.hub && t.status !== 'today_direct');
        const uploadDates = new Set(hubTxns.map(t => t.collection_date));
        const futureDeposited = directSubs
          .filter(s => !s.txn_date || !uploadDates.has(s.txn_date))
          .reduce((a, s) => a + Number(s.amount), 0);
        const matchedDirectAmt = directSubs
          .filter(s => s.txn_date && uploadDates.has(s.txn_date))
          .reduce((a, s) => a + Number(s.amount), 0);
        const baseSubmitted = hubTxns.length > 0
          ? computeSubmitted(hubTxns).submitted
          : subs.filter(s => !s.is_direct).reduce((a, s) => a + Number(s.amount), 0);
        const submitted = baseSubmitted + matchedDirectAmt;
        const totalToCollect = hubTxns.reduce((s, t) => s + Number(t.amount_to_collect || 0), 0);
        const pendency  = Math.max(0, totalToCollect - submitted);
        const lastSub   = subs.length > 0 ? subs[subs.length - 1] : null;
        return { ...h, total_to_collect: totalToCollect, totalToCollect, submitted, futureDeposited, pendency, lastSub };
      });
    }

    const [mName, yy] = summaryMonth.split('-');
    const mIdx    = MN.indexOf(mName);
    const fullYear = 2000 + parseInt(yy, 10);
    const hubAgg = {};
    summaryTxns.filter(t => t.month === summaryMonth).forEach(t => {
      if (!hubAgg[t.hub]) {
        const meta = hubData.find(h => h.hub === t.hub) || {};
        hubAgg[t.hub] = { hub: t.hub, zone: t.zone || meta.zone, hub_type: t.hub_type || meta.hub_type, total_diff: 0, total_to_collect: 0, total_deposited: 0 };
      }
      hubAgg[t.hub].total_diff       += t.diff              || 0;
      hubAgg[t.hub].total_to_collect += t.amount_to_collect || 0;
      hubAgg[t.hub].total_deposited  += t.deposited         || 0;
    });
    return Object.values(hubAgg).map(h => {
      const allSubs = subMap[h.hub] || [];
      const direct  = allSubs.filter(s => {
        if (!s.txn_date || !s.is_direct) return false;
        const [sy, sm] = s.txn_date.split('-').map(Number);
        return sy === fullYear && sm === mIdx + 1;
      });
      const hubMonthTxns = summaryTxns.filter(t => t.hub === h.hub && t.status !== 'today_direct' && t.month === summaryMonth);
      const uploadDates  = new Set(hubMonthTxns.map(t => t.collection_date));
      const futureDeposited  = direct.filter(s => !s.txn_date || !uploadDates.has(s.txn_date)).reduce((a, s) => a + Number(s.amount), 0);
      const matchedDirectAmt = direct.filter(s => s.txn_date && uploadDates.has(s.txn_date)).reduce((a, s) => a + Number(s.amount), 0);
      const { submitted: base, countedIds } = hubMonthTxns.length > 0 ? computeSubmitted(hubMonthTxns) : { submitted: 0, countedIds: new Set() };
      const submitted = base + matchedDirectAmt;
      const pendency  = Math.max(0, (h.total_to_collect || 0) - submitted);
      const lastSub   = direct.length > 0 ? direct[direct.length - 1] : null;
      return { ...h, submitted, futureDeposited, pendency, lastSub, subCount: countedIds.size };
    });
  }, [hubData, transactions, summaryMonth, subMap, subHubOrphan]); // eslint-disable-line

  const filtered = enriched
    .filter(h => !search || h.hub?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => (b.pendency || 0) - (a.pendency || 0));

  // ── History tab ────────────────────────────────────────────────────────────
  // hub|date → first real transaction (for resolving total_to_collect for direct subs)
  const txnByHubDate = useMemo(() => {
    const m = {};
    transactions.forEach(t => {
      if (t.status === 'today_direct') return;
      const key = `${t.hub}|${t.collection_date}`;
      if (!m[key]) m[key] = t;
    });
    return m;
  }, [transactions]);

  const histHubs = useMemo(() =>
    ['All', ...[...new Set(submissions.map(s => s.hub).filter(Boolean))].sort()],
    [submissions]
  );
  const histMonths = useMemo(() => {
    const seen = new Set();
    submissions.forEach(s => { const m = histSubMonth(s); if (m) seen.add(m); });
    return ['All', ...[...seen].sort().reverse()];
  }, [submissions]); // eslint-disable-line
  // Set of "hub|collection_date" keys that are NOT closed in the uploaded file
  const openDatesSet = useMemo(() => {
    const CLOSED = ['CLOSED', 'Closed', 'closed'];
    const s = new Set();
    transactions.forEach(t => {
      if (!CLOSED.includes(t.line_status) && t.hub && t.collection_date)
        s.add(`${t.hub}|${t.collection_date}`);
    });
    return s;
  }, [transactions]); // eslint-disable-line

  const filteredSubs = useMemo(() => {
    const filtered = submissions.filter(s => {
      if (histHub      !== 'All' && s.hub !== histHub)              return false;
      if (histMonth    !== 'All' && histSubMonth(s) !== histMonth)  return false;
      if (histKind     !== 'All' && histSubKind(s)  !== histKind)   return false;
      const baseDate = s.txn_date || s.submission_date;
      if (histDateFrom && (!baseDate || baseDate < histDateFrom))   return false;
      if (histDateTo   && (!baseDate || baseDate > histDateTo))     return false;
      if (histOpenOnly && !openDatesSet.has(`${s.hub}|${s.txn_date}`)) return false;
      return true;
    });
    // Sort: newest submission date at the top (descending by submission_date, then created_at)
    filtered.sort((a, b) => {
      const da = a.submission_date || a.txn_date || '';
      const db = b.submission_date || b.txn_date || '';
      if (db !== da) return db.localeCompare(da);
      // tiebreak: most recently created first
      return (b.created_at || '').localeCompare(a.created_at || '');
    });
    return filtered;
  }, [submissions, histHub, histMonth, histKind, histDateFrom, histDateTo, histOpenOnly, openDatesSet]); // eslint-disable-line

  // All currently-flagged submissions
  const flaggedSubs = useMemo(() => submissions.filter(s => s.review_status === 'flagged'), [submissions]);

  async function handleFlagSubmission(subId) {
    setFlagging(true);
    try {
      await flagSubmissionForReview(subId, flagNote.trim() || null);
      setSubmissions(prev => prev.map(s => s.id === subId
        ? { ...s, review_status: 'flagged', review_note: flagNote.trim() || null }
        : s
      ));
      setFlagSubId(null);
      setFlagNote('');
    } catch (e) { alert('Failed to send back: ' + e.message); }
    setFlagging(false);
  }

  async function handleClearFlag(subId) {
    try {
      await clearSubmissionReview(subId);
      setSubmissions(prev => prev.map(s => s.id === subId
        ? { ...s, review_status: null, review_note: null }
        : s
      ));
    } catch (e) { alert('Failed to clear flag: ' + e.message); }
  }

  const inputStyle = {
    background: '#1a1f2e', color: '#f3f4f6', border: '1px solid #2a2f3e',
    borderRadius: 8, padding: '8px 12px', fontSize: 12, fontFamily: "'DM Mono', monospace", outline: 'none',
  };

  // ── Render ─────────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: '#0b0e14', fontFamily: "'DM Mono', monospace", paddingBottom: selectedCells.size > 0 ? 48 : 0 }}>

      {/* Submission modal */}
      {submitModal && (
        <SubmitModal
          txn={submitModal.txn}
          uploadId={uploadMeta?.id}
          hub={submitModal.hub}
          directDate={submitModal.directDate}
          defaultAmount={submitModal.defaultAmount || 0}
          managerName={user.username}
          onClose={() => setSubmitModal(null)}
          onSuccess={() => { setSubmitModal(null); fetchData(); }}
        />
      )}

      {/* Top bar */}
      <div style={{ background: '#0f1117', borderBottom: '1px solid #1e2433', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ color: '#f59e0b', fontWeight: 900, fontSize: 16, letterSpacing: 2 }}>WHEELEY</div>
            <div style={{ color: '#2a2f3e', fontSize: 14 }}>|</div>
            <div style={{ color: '#6b7280', fontSize: 11, letterSpacing: 1 }}>MANAGER DASHBOARD</div>
          </div>
          {/* ── Section switcher ── */}
          <div style={{ display: 'flex', background: '#0b0e14', border: '1px solid #1e2433', borderRadius: 10, padding: 3, gap: 2 }}>
            {[['cod', '📦 COD Reconciliation'], ['payment', '💳 Payment Sheet']].map(([k, label]) => (
              <button key={k} onClick={() => setSection(k)}
                style={{
                  padding: '6px 14px', border: 'none', borderRadius: 8, cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 11, fontWeight: 700, letterSpacing: 0.5, transition: 'all .15s',
                  background: section === k ? (k === 'payment' ? '#1e3a5f' : '#1c1a00') : 'transparent',
                  color: section === k ? (k === 'payment' ? '#60a5fa' : '#f59e0b') : '#4b5563',
                  boxShadow: section === k ? '0 1px 4px #00000060' : 'none',
                }}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: '#6b7280', fontSize: 11 }}>
            👤 <span style={{ color: '#f3f4f6', fontWeight: 700 }}>{user.username}</span>
          </span>
          <button onClick={onLogout}
            style={{ background: 'transparent', color: '#6b7280', border: '1px solid #2a2f3e', borderRadius: 8, padding: '6px 14px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>
            Sign out
          </button>
        </div>
      </div>

      {section === 'payment' && <PaymentSheet isAdmin={false} user={user} />}

      {section === 'cod' && (<>
      {/* ── Manager Tabs ─────────────────────────────────────────────────────── */}
      <div style={{ background: '#0f1117', borderBottom: '1px solid #1e2433', padding: '0 24px', display: 'flex', gap: 0 }}>
        {[
          ['summary', '📊 Hub Summary'],
          ['history', '🕐 Submission History'],
          ['flagged', '⚑ Flagged'],
        ].map(([k, label]) => (
          <button key={k} onClick={() => setManagerView(k)}
            style={{
              padding: '13px 22px', background: 'transparent', border: 'none',
              borderBottom: `2px solid ${managerView === k ? (k === 'flagged' ? '#f97316' : '#f59e0b') : 'transparent'}`,
              color: managerView === k ? (k === 'flagged' ? '#f97316' : '#f59e0b') : (k === 'flagged' && flaggedSubs.length > 0 ? '#f87171' : '#6b7280'),
              fontSize: 12, fontWeight: 700, letterSpacing: 1, cursor: 'pointer',
              fontFamily: 'inherit', transition: 'all .2s',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
            {label}
            {k === 'flagged' && flaggedSubs.length > 0 && (
              <span style={{ background: managerView === k ? '#f97316' : '#7f1d1d', color: managerView === k ? '#0f1117' : '#f87171', borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 900 }}>
                {flaggedSubs.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {managerView === 'summary' && (
      <div style={{ padding: '24px 24px 32px', display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 1600, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ color: '#f3f4f6', fontSize: 20, fontWeight: 900 }}>Hub Summary</div>
            <div style={{ color: '#4b5563', fontSize: 11, marginTop: 3 }}>
              {managedHubs.length} hub{managedHubs.length !== 1 ? 's' : ''} under your management
              {uploadMeta && <span style={{ color: '#374151' }}> · Upload: {uploadMeta.filename}</span>}
            </div>
          </div>
          <button onClick={fetchData}
            style={{ background: '#1a1f2e', color: '#9ca3af', border: '1px solid #2a2f3e', borderRadius: 8, padding: '8px 16px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>
            ↻ Refresh
          </button>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input placeholder="Search hub..." value={search} onChange={e => setSearch(e.target.value)} style={{ ...inputStyle, width: 200 }} />
          <select value={summaryMonth} onChange={e => setSummaryMonth(e.target.value)} style={{ ...inputStyle, fontWeight: summaryMonth !== 'All' ? 700 : 400, color: summaryMonth !== 'All' ? '#818cf8' : '#f3f4f6' }}>
            {summaryMonths.map(m => <option key={m}>{m}</option>)}
          </select>
          {summaryMonth !== 'All' && (
            <button onClick={() => setSummaryMonth('All')} style={{ background: '#1a1f2e', color: '#9ca3af', border: '1px solid #2a2f3e', borderRadius: 8, padding: '8px 14px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
              ✕ Clear Month
            </button>
          )}
          <div style={{ marginLeft: 'auto', color: '#4b5563', fontSize: 12 }}>
            {filtered.length} hub{filtered.length !== 1 ? 's' : ''} · {summaryMonth !== 'All' ? summaryMonth : 'All months'}
          </div>
        </div>

        {/* Hub Summary Table */}
        {loading ? <Spinner /> : (
          <div style={{ background: '#0f1117', border: '1px solid #1e2433', borderRadius: 12, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#1a1f2e' }}>
                  {['HUB', 'ZONE', 'HUB TYPE', 'TO COLLECT', 'DEPOSITED', 'DIFF', 'SUBMITTED', 'FUTURE DEP.', 'PENDENCY', 'STATUS', 'LAST SUBMISSION'].map(h => (
                    <th key={h} style={{ color: h === 'FUTURE DEP.' ? '#6366f1' : '#4b5563', fontSize: 10, letterSpacing: 1, padding: '12px 16px', textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((h, i) => {
                  const toCollect  = h.total_to_collect || h.totalToCollect || 0;
                  const deposited  = h.total_deposited  || h.totalDeposited  || 0;
                  const diff       = h.total_diff       || h.totalDiff       || 0;
                  const pct        = toCollect > 0 ? Math.min(100, (h.submitted / toCollect) * 100) : 100;
                  const isExpanded = expandedHub === h.hub;

                  const hubTxns = transactions
                    .filter(t => t.hub === h.hub && t.status !== 'today_direct' && (summaryMonth === 'All' || t.month === summaryMonth))
                    .sort((a, b) => a.collection_date.localeCompare(b.collection_date));

                  // Untracked dates — dates after last Excel date up to today
                  const missingDates = getMissingDates(hubTxns);

                  return (
                    <>
                    <tr key={h.hub}
                      onClick={() => setExpandedHub(isExpanded ? null : h.hub)}
                      style={{ borderTop: '1px solid #1e2433', background: isExpanded ? '#111827' : i % 2 === 0 ? '#0f1117' : '#0d101a', cursor: 'pointer', transition: 'background .15s' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ color: '#4b5563', fontSize: 10, transition: 'transform .2s', display: 'inline-block', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                          <div style={{ color: '#f3f4f6', fontSize: 13, fontWeight: 600 }}>{h.hub}</div>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ background: h.zone === 'North' ? '#0c1a2e' : '#1c0e00', color: h.zone === 'North' ? '#60a5fa' : '#fb923c', borderRadius: 6, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>{h.zone}</span>
                      </td>
                      <td style={{ padding: '12px 16px', color: '#6b7280', fontSize: 12 }}>{h.hub_type || h.hubType || '–'}</td>
                      <td style={{ padding: '12px 16px', color: '#60a5fa', fontSize: 13 }}>{fmt(toCollect)}</td>
                      <td style={{ padding: '12px 16px', color: '#a78bfa', fontSize: 13 }}>{fmt(deposited)}</td>
                      <td style={{ padding: '12px 16px', color: '#fb923c', fontSize: 13 }}>{fmt(diff)}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ color: '#34d399', fontSize: 13 }}>{fmt(h.submitted)}</div>
                        <div style={{ background: '#1a1f2e', borderRadius: 4, height: 4, width: 80, marginTop: 4 }}>
                          <div style={{ background: '#34d399', width: `${pct}%`, height: '100%', borderRadius: 4 }} />
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {h.futureDeposited > 0
                          ? <span style={{ color: '#818cf8', fontSize: 13, fontWeight: 700 }}>{fmt(h.futureDeposited)}</span>
                          : <span style={{ color: '#374151', fontSize: 12 }}>–</span>}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ color: h.pendency > 0 ? '#f87171' : '#34d399', fontSize: 13, fontWeight: 700 }}>{fmt(h.pendency)}</span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {h.pendency === 0
                          ? <span style={{ background: '#064e3b', color: '#34d399', borderRadius: 6, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>CLEARED</span>
                          : (subMap[h.hub] || []).filter(s => !s.is_direct).length === 0
                            ? <span style={{ background: '#7f1d1d', color: '#f87171', borderRadius: 6, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>NO SUBMISSION</span>
                            : <span style={{ background: '#451a03', color: '#fb923c', borderRadius: 6, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>PARTIAL</span>}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#6b7280', fontSize: 12 }}>
                        {h.lastSub ? `${h.lastSub.type?.toUpperCase()} · ${h.lastSub.submission_date}` : '–'}
                      </td>
                    </tr>

                    {/* ── Expanded breakdown ── */}
                    {isExpanded && (
                      <tr key={`${h.hub}-exp`}>
                        <td colSpan={11} style={{ padding: 0, background: '#0b0f18' }}>
                          {hubTxns.length === 0 ? (
                            <div style={{ padding: '16px 32px', color: '#4b5563', fontSize: 12 }}>No transaction data for this hub{summaryMonth !== 'All' ? ` in ${summaryMonth}` : ''}.</div>
                          ) : (
                            <>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                              <thead>
                                <tr style={{ background: '#0f1117' }}>
                                  {['DATE', 'MONTH', 'TOTAL COLLECTION', 'SUBMITTED', 'SHORTFALL', 'EXCESS', 'STATUS', 'SUBMISSIONS', 'ACTION'].map(col => (
                                    <th key={col} style={{ color: col === 'ACTION' ? '#fbbf24' : col === 'EXCESS' ? '#3b82f6' : '#374151', fontSize: 10, letterSpacing: 1, padding: '8px 16px', textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap' }}>{col}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {(() => {
                                  // Group same-date rows into one display row per date.
                                  // Skip dates where ALL rows have line_status Closed.
                                  const CLOSED_LS = ['CLOSED', 'Closed', 'closed'];
                                  const dateGroups = {};
                                  hubTxns.forEach(t => {
                                    const key = t.collection_date || '__no_date__';
                                    if (!dateGroups[key]) dateGroups[key] = [];
                                    dateGroups[key].push(t);
                                  });
                                  return Object.values(dateGroups)
                                    .filter(group => group.some(t => !CLOSED_LS.includes(t.line_status)))
                                    .sort((a, b) =>
                                      (a[0].collection_date || '').localeCompare(b[0].collection_date || ''));
                                })().map(group => {
                                  const t        = group[0];
                                  const rowKey   = group.map(tx => tx.id).join('-');
                                  const due      = group.reduce((s, tx) => s + Number(tx.amount_to_collect || 0), 0);
                                  const allDateSubs = subByHubDate[`${t.hub}|${t.collection_date}`] || [];
                                  const orphanFallback = allDateSubs.length === 0 && due > 0
                                    ? (subHubOrphan[t.hub] || []).filter(s =>
                                        group.some(tx => Number(s.amount) === Number(tx.amount_to_collect)))
                                    : [];
                                  const txnSubs    = allDateSubs.length > 0 ? allDateSubs : orphanFallback;
                                  const directSubs = subByHubDateDirect[`${t.hub}|${t.collection_date}`] || [];
                                  const allSubs    = [...txnSubs, ...directSubs];
                                  const subTotal   = allSubs.reduce((a, s) => a + Number(s.amount), 0);
                                  const shortfall  = Math.max(0, due - subTotal);
                                  const excess     = Math.max(0, subTotal - due);
                                  const remaining  = shortfall;
                                  const cleared    = shortfall === 0 && due > 0;
                                  const noSub      = allSubs.length === 0;
                                  const typeMap    = { cms: 'CMS', neft_flipkart: 'NEFT-FK', neft_wheeley: 'NEFT-WH' };
                                  // For submit: prefer first transaction that still has remaining diff
                                  const primaryForSub = group.find(tx => Number(tx.diff) > 0) || group[0];

                                  return (
                                    <tr key={rowKey} style={{ borderTop: '1px solid #1e243380' }}>
                                      <td style={{ padding: '9px 16px', color: '#d1d5db', fontWeight: 600 }}>{t.collection_date}</td>
                                      <td style={{ padding: '9px 16px', color: '#6b7280' }}>{t.month || '–'}</td>
                                      {/* TOTAL COLLECTION — clickable */}
                                      {(() => {
                                        const ck = `mgr-${rowKey}-due`;
                                        const isSel = selectedCells.has(ck);
                                        return (
                                          <td style={{ padding: '9px 16px', cursor: due > 0 ? 'cell' : 'default',
                                              background: isSel ? '#1e3a1e' : 'transparent',
                                              outline: isSel ? '2px solid #34d39970' : 'none', outlineOffset: -2 }}
                                            onClick={() => { if (due > 0) toggleCell(ck, due); }}
                                            title={due > 0 ? 'Click to add to sum' : undefined}>
                                            <span style={{ color: '#60a5fa', fontWeight: isSel ? 900 : 400 }}>{fmt(due)}</span>
                                          </td>
                                        );
                                      })()}
                                      {/* SUBMITTED — clickable */}
                                      {(() => {
                                        const ck = `mgr-${rowKey}-sub`;
                                        const isSel = selectedCells.has(ck);
                                        return (
                                          <td style={{ padding: '9px 16px', cursor: subTotal > 0 ? 'cell' : 'default',
                                              background: isSel ? '#1e3a1e' : 'transparent',
                                              outline: isSel ? '2px solid #34d39970' : 'none', outlineOffset: -2 }}
                                            onClick={() => { if (subTotal > 0) toggleCell(ck, subTotal); }}
                                            title={subTotal > 0 ? 'Click to add to sum' : undefined}>
                                            {subTotal > 0
                                              ? <span style={{ color: '#34d399', fontWeight: isSel ? 900 : 400 }}>{fmt(subTotal)}</span>
                                              : <span style={{ color: '#374151' }}>–</span>}
                                          </td>
                                        );
                                      })()}
                                      {/* SHORTFALL — clickable */}
                                      {(() => {
                                        const ck = `mgr-${rowKey}-short`;
                                        const isSel = selectedCells.has(ck);
                                        return (
                                          <td style={{ padding: '9px 16px', cursor: due > 0 ? 'cell' : 'default',
                                              background: isSel ? '#1e3a1e' : 'transparent',
                                              outline: isSel ? '2px solid #34d39970' : 'none', outlineOffset: -2 }}
                                            onClick={() => { if (due > 0) toggleCell(ck, shortfall); }}
                                            title={due > 0 ? 'Click to add to sum' : undefined}>
                                            {due === 0
                                              ? <span style={{ color: '#374151' }}>–</span>
                                              : <span style={{ color: shortfall > 0 ? '#f87171' : '#34d399', fontWeight: isSel ? 900 : 700 }}>{fmt(shortfall)}</span>}
                                          </td>
                                        );
                                      })()}
                                      {/* EXCESS — clickable */}
                                      {(() => {
                                        const ck = `mgr-${rowKey}-excess`;
                                        const isSel = selectedCells.has(ck);
                                        return (
                                          <td style={{ padding: '9px 16px', cursor: excess > 0 ? 'cell' : 'default',
                                              background: isSel ? '#0c1a2e' : 'transparent',
                                              outline: isSel ? '2px solid #3b82f670' : 'none', outlineOffset: -2 }}
                                            onClick={() => { if (excess > 0) toggleCell(ck, excess); }}
                                            title={excess > 0 ? 'Click to add to sum' : undefined}>
                                            {excess > 0
                                              ? <span style={{ color: '#3b82f6', fontWeight: isSel ? 900 : 700 }}>{fmt(excess)}</span>
                                              : <span style={{ color: '#374151' }}>–</span>}
                                          </td>
                                        );
                                      })()}
                                      <td style={{ padding: '9px 16px' }}>
                                        {due === 0
                                          ? <span style={{ color: '#374151', fontSize: 10 }}>N/A</span>
                                          : cleared
                                            ? <span style={{ background: '#065f46', color: '#34d399', borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>CLEARED</span>
                                            : noSub
                                              ? <span style={{ background: '#7f1d1d', color: '#f87171', borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>PENDING</span>
                                              : <span style={{ background: '#451a03', color: '#fb923c', borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>PARTIAL</span>}
                                      </td>
                                      <td style={{ padding: '9px 16px' }}>
                                        {allSubs.length === 0
                                          ? <span style={{ color: '#374151', fontSize: 11 }}>No submissions yet</span>
                                          : <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                              {allSubs.map(s => {
                                                const _rc      = (s.remarks || '').trim().replace(/^\[ADMIN\]\s*/i, '').trim();
                                                const isLoss   = _rc.startsWith('[LOSS]');
                                                const isShort  = _rc.startsWith('[SHORT]');
                                                const isExcess = _rc.startsWith('[EXCESS]');
                                                const isAdminSub = s.remarks?.trim().startsWith('[ADMIN]') || s.submitted_by?.startsWith('manager:');
                                                const baseType = typeMap[s.type] || s.type?.toUpperCase() || '—';
                                                const typeLabel = isLoss ? 'LOSS/UTILIZED' : isExcess ? 'EXCESS' : baseType;
                                                const typeColor = isLoss ? '#f97316' : isExcess ? '#3b82f6' : '#60a5fa';
                                                const cleanRemarks = _rc.replace(/^\[(LOSS|SHORT|EXCESS)\]\s*/i, '').trim();
                                                return (
                                                  <div key={s.id} style={{ display: 'flex', flexDirection: 'column', gap: 3, paddingBottom: 5, borderBottom: '1px solid #1e243350' }}>
                                                    {/* Info + action buttons row — single line, info truncates */}
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, overflow: 'hidden' }}>
                                                      <span title={[typeLabel, fmt(Number(s.amount)), s.submission_date, s.ref_number ? `#${s.ref_number}` : '', cleanRemarks].filter(Boolean).join(' · ')}
                                                        style={{ color: '#9ca3af', fontSize: 11, flex: 1, minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                        <span style={{ color: typeColor, fontWeight: 700 }}>{typeLabel}</span>
                                                        {isShort  && <span style={{ color: '#f87171', fontWeight: 700 }}> · SHORT</span>}
                                                        {isAdminSub && <span style={{ color: '#fbbf24', fontSize: 9 }}> MGR</span>}
                                                        {' · '}{fmt(Number(s.amount))}
                                                        {' · '}{s.submission_date}
                                                        {!isLoss && s.ref_number ? <span style={{ color: '#6b7280' }}> · #{s.ref_number}</span> : ''}
                                                        {cleanRemarks ? <span style={{ color: '#6b7280', fontStyle: 'italic' }}> · {cleanRemarks}</span> : ''}
                                                      </span>
                                                      {/* View Proof */}
                                                      {s.slip_url && (
                                                        <button onClick={() => setPreviewSlip(s.slip_url)}
                                                          style={{ color: '#f59e0b', fontSize: 10, fontWeight: 700, background: '#1c150080', border: '1px solid #f59e0b40', borderRadius: 4, padding: '1px 6px', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                                                          📎 Proof
                                                        </button>
                                                      )}
                                                      {/* Send Back / Unflag */}
                                                      {s.review_status === 'flagged' ? (
                                                        <button onClick={() => handleClearFlag(s.id)}
                                                          style={{ background: '#1e3a2f', color: '#34d399', border: '1px solid #34d39930', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                                                          ↩ Unflag
                                                        </button>
                                                      ) : flagSubId !== s.id && (
                                                        <button onClick={() => { setFlagSubId(s.id); setFlagNote(''); }}
                                                          style={{ background: 'transparent', color: '#4b5563', border: '1px solid #1e2433', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
                                                          onMouseEnter={e => { e.currentTarget.style.borderColor = '#f97316'; e.currentTarget.style.color = '#f97316'; }}
                                                          onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e2433'; e.currentTarget.style.color = '#4b5563'; }}>
                                                          ⚑ Send Back
                                                        </button>
                                                      )}
                                                    </div>
                                                    {/* Review note when flagged */}
                                                    {s.review_status === 'flagged' && s.review_note && (
                                                      <div style={{ color: '#f87171', fontSize: 10, fontStyle: 'italic' }}>"{s.review_note}"</div>
                                                    )}
                                                    {/* Inline send-back form */}
                                                    {flagSubId === s.id && (
                                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                        <input placeholder="Reason (optional)" value={flagNote}
                                                          onChange={e => setFlagNote(e.target.value)}
                                                          style={{ background: '#0b0e14', color: '#f3f4f6', border: '1px solid #f8717160', borderRadius: 6, padding: '4px 8px', fontSize: 11, fontFamily: 'inherit', outline: 'none', width: 150 }} />
                                                        <div style={{ display: 'flex', gap: 5 }}>
                                                          <button onClick={() => handleFlagSubmission(s.id)} disabled={flagging}
                                                            style={{ background: '#7f1d1d', color: '#f87171', border: '1px solid #f8717140', borderRadius: 6, padding: '4px 10px', fontSize: 10, fontWeight: 700, cursor: flagging ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
                                                            {flagging ? '...' : 'Send Back'}
                                                          </button>
                                                          <button onClick={() => { setFlagSubId(null); setFlagNote(''); }}
                                                            style={{ background: 'transparent', color: '#6b7280', border: '1px solid #2a2f3e', borderRadius: 6, padding: '4px 7px', fontSize: 10, cursor: 'pointer', fontFamily: 'inherit' }}>
                                                            ✕
                                                          </button>
                                                        </div>
                                                      </div>
                                                    )}
                                                  </div>
                                                );
                                              })}
                                            </div>}
                                      </td>
                                      {/* ACTION — submit button */}
                                      <td style={{ padding: '9px 16px' }} onClick={e => e.stopPropagation()}>
                                        <button
                                          onClick={() => setSubmitModal({ hub: t.hub, txn: primaryForSub, directDate: null, defaultAmount: remaining })}
                                          style={{ background: '#1c1a00', color: '#fbbf24', border: '1px solid #3d3000', borderRadius: 6, padding: '4px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                                          ＋ Submit
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>

                            {/* Untracked / direct deposit section */}
                            <div style={{ borderTop: '1px solid #1e243360', padding: '10px 16px', background: '#0a0d14' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                <span style={{ color: '#374151', fontSize: 10, letterSpacing: 1 }}>DIRECT DEPOSIT (UNTRACKED DATES)</span>
                                {missingDates.length > 0 && (
                                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                    {missingDates.map(date => {
                                      const existingDirect = subByHubDateDirect[`${h.hub}|${date}`] || [];
                                      return (
                                        <button key={date}
                                          onClick={e => { e.stopPropagation(); setSubmitModal({ hub: h.hub, txn: null, directDate: date, defaultAmount: 0 }); }}
                                          style={{ background: '#0c1a2e', color: '#60a5fa', border: '1px solid #1e3a5f', borderRadius: 6, padding: '3px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                                          ＋ {date}{existingDirect.length > 0 ? ` (${existingDirect.length})` : ''}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                                {/* Generic direct deposit button for any date */}
                                <button
                                  onClick={e => { e.stopPropagation(); setSubmitModal({ hub: h.hub, txn: null, directDate: todayStr(), defaultAmount: 0 }); }}
                                  style={{ background: '#1a1a2e', color: '#818cf8', border: '1px solid #312e81', borderRadius: 6, padding: '3px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                                  ＋ Other Date…
                                </button>
                              </div>
                            </div>
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )} {/* end managerView === 'summary' */}

      {/* ── Submission History tab ────────────────────────────────────────────── */}
      {managerView === 'history' && (
        <div style={{ padding: '24px 24px 32px', display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 1600, margin: '0 auto' }}>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={histHub} onChange={e => setHistHub(e.target.value)} style={inputStyle}>
              {histHubs.map(h => <option key={h}>{h}</option>)}
            </select>
            <select value={histMonth} onChange={e => setHistMonth(e.target.value)} style={inputStyle}>
              {histMonths.map(m => <option key={m}>{m}</option>)}
            </select>
            <select value={histKind} onChange={e => setHistKind(e.target.value)} style={inputStyle}>
              {['All', 'Full', 'Short', 'Excess', 'Loss'].map(k => <option key={k}>{k}</option>)}
            </select>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: '#4b5563', fontSize: 11 }}>From</span>
              <input type="date" value={histDateFrom} onChange={e => setHistDateFrom(e.target.value)}
                style={{ ...inputStyle, padding: '8px 10px', width: 138 }} />
              <span style={{ color: '#4b5563', fontSize: 11 }}>To</span>
              <input type="date" value={histDateTo} onChange={e => setHistDateTo(e.target.value)}
                style={{ ...inputStyle, padding: '8px 10px', width: 138 }} />
            </div>
            {/* Open dates toggle */}
            <button onClick={() => setHistOpenOnly(v => !v)}
              style={{ ...inputStyle, cursor: 'pointer', fontSize: 11, fontWeight: 700, letterSpacing: 1,
                background: histOpenOnly ? '#14532d' : '#1a1f2e',
                color: histOpenOnly ? '#34d399' : '#f59e0b',
                border: `1px solid ${histOpenOnly ? '#34d399' : '#f59e0b'}`,
                whiteSpace: 'nowrap',
              }}>
              {histOpenOnly ? '✓ OPEN DATES' : '○ OPEN DATES'}
            </button>
            {(histHub !== 'All' || histMonth !== 'All' || histKind !== 'All' || histDateFrom || histDateTo || histOpenOnly) && (
              <button onClick={() => { setHistHub('All'); setHistMonth('All'); setHistKind('All'); setHistDateFrom(''); setHistDateTo(''); setHistOpenOnly(false); }}
                style={{ ...inputStyle, cursor: 'pointer', fontSize: 11, color: '#f87171' }}>✕ Clear</button>
            )}
            <div style={{ marginLeft: 'auto', color: '#4b5563', fontSize: 12, alignSelf: 'center', whiteSpace: 'nowrap' }}>
              {filteredSubs.length} rows
            </div>
          </div>

          {/* Table */}
          <div style={{ background: '#0f1117', border: '1px solid #1e2433', borderRadius: 12, overflow: 'auto' }}>
            {loading ? <Spinner /> : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#1a1f2e' }}>
                    {['HUB', 'FOR DATE', 'TOTAL COLLECTION', 'KIND', 'PAYMENT', 'SUBMITTED', 'SHORT AMT', 'SUB DATE', 'REF / UTR', 'REMARKS', 'SLIP', 'SUBMITTED AT', 'SEND BACK'].map(col => (
                      <th key={col} style={{ color: col === 'SEND BACK' ? '#f97316' : '#4b5563', fontSize: 10, letterSpacing: 1, padding: '12px 16px', textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap' }}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredSubs.length === 0 ? (
                    <tr><td colSpan={13} style={{ color: '#4b5563', textAlign: 'center', padding: 40 }}>No submissions match the filters</td></tr>
                  ) : filteredSubs.map((s, i) => {
                    const kind = histSubKind(s);
                    const rawRemarks = (s.remarks || '').replace(/^\[ADMIN\]\s*/i, '').trim();
                    const displayRemarks = rawRemarks.replace(/^\[(LOSS|SHORT|EXCESS)\]\s*/i, '').trim() || null;
                    const kindColor = kind === 'Loss'   ? { bg: '#1c0e00', text: '#f97316' }
                      : kind === 'Short'  ? { bg: '#2d0f0f', text: '#f87171' }
                      : kind === 'Excess' ? { bg: '#0c1a2e', text: '#3b82f6' }
                      : { bg: '#0c1c14', text: '#34d399' };
                    const typeMapH   = { cms: 'CMS', neft_flipkart: 'NEFT-FK', neft_wheeley: 'NEFT-WH' };
                    const isNeft     = s.type?.includes('neft');
                    const typeLabel  = typeMapH[s.type] || s.type?.toUpperCase() || '—';
                    const resolvedAmt = s.txn_amount_to_collect ??
                      (s.is_direct && s.txn_date ? txnByHubDate[`${s.hub}|${s.txn_date}`]?.amount_to_collect : null);
                    const shortAmt = resolvedAmt != null ? Math.max(0, Number(resolvedAmt) - Number(s.amount)) : null;
                    return (
                      <tr key={s.id} style={{ borderTop: `1px solid ${s.review_status === 'flagged' ? '#7f1d1d' : '#1e2433'}`, background: s.review_status === 'flagged' ? '#1a0808' : (i % 2 === 0 ? '#0f1117' : '#0d101a') }}>
                        {/* HUB */}
                        <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                          <div style={{ color: '#f3f4f6', fontSize: 13 }}>{s.hub}</div>
                          {s.review_status === 'flagged' && (
                            <div style={{ background: '#7f1d1d', color: '#f87171', borderRadius: 4, padding: '1px 7px', fontSize: 9, fontWeight: 700, display: 'inline-block', marginTop: 3, letterSpacing: 1 }}>⚑ SENT BACK</div>
                          )}
                        </td>
                        {/* FOR DATE */}
                        <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                          {s.txn_date
                            ? <span style={{ color: '#9ca3af', fontSize: 13 }}>{s.txn_date}</span>
                            : <span style={{ color: '#374151', fontSize: 12 }}>–</span>}
                          {s.is_direct && !txnByHubDate[`${s.hub}|${s.txn_date}`] && (
                            <div style={{ color: '#6366f1', fontSize: 9, fontWeight: 700, marginTop: 2 }}>UNTRACKED</div>
                          )}
                        </td>
                        {/* TOTAL COLLECTION */}
                        <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                          {resolvedAmt != null
                            ? <span style={{ color: '#60a5fa', fontSize: 13, fontWeight: 700 }}>{fmt(resolvedAmt)}</span>
                            : <span style={{ color: '#374151', fontSize: 12 }}>–</span>}
                        </td>
                        {/* KIND */}
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ background: kindColor.bg, color: kindColor.text, borderRadius: 5, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>
                            {kind === 'Loss' ? '💸 LOSS' : kind === 'Short' ? '⚠️ SHORT' : kind === 'Excess' ? '💰 EXCESS' : '✅ FULL'}
                          </span>
                        </td>
                        {/* PAYMENT */}
                        <td style={{ padding: '12px 16px' }}>
                          {kind === 'Loss'
                            ? <span style={{ background: '#1c0e00', color: '#f97316', borderRadius: 5, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>LOSS/UTILIZED</span>
                            : <span style={{ background: isNeft ? '#0c1a2e' : '#1c1a00', color: isNeft ? '#60a5fa' : '#fbbf24', borderRadius: 5, padding: '2px 8px', fontSize: 10, fontWeight: 700, border: `1px solid ${isNeft ? '#1e3a5f' : '#3d3000'}` }}>
                                {typeLabel}
                              </span>}
                        </td>
                        {/* SUBMITTED */}
                        <td style={{ padding: '12px 16px', color: kind === 'Loss' ? '#f97316' : '#34d399', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {fmt(s.amount)}
                        </td>
                        {/* SHORT AMT */}
                        <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                          {shortAmt != null
                            ? <span style={{ color: shortAmt > 0 ? '#f87171' : '#34d399', fontWeight: 700 }}>{shortAmt > 0 ? fmt(shortAmt) : '₹0'}</span>
                            : <span style={{ color: '#374151', fontSize: 12 }}>–</span>}
                        </td>
                        {/* SUB DATE */}
                        <td style={{ padding: '12px 16px', color: '#9ca3af', fontSize: 13, whiteSpace: 'nowrap' }}>{s.submission_date}</td>
                        {/* REF */}
                        <td style={{ padding: '12px 16px', color: '#6b7280', fontSize: 12 }}>{s.ref_number || '–'}</td>
                        {/* REMARKS */}
                        <td style={{ padding: '12px 16px', maxWidth: 220 }}>
                          {displayRemarks
                            ? <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ color: '#6b7280', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>
                                  {displayRemarks}
                                </span>
                                {(displayRemarks?.length || 0) > 30 && (
                                  <button onClick={() => setViewRemarks(displayRemarks)}
                                    style={{ flexShrink: 0, background: '#1a1f2e', color: '#9ca3af', border: '1px solid #2a2f3e', borderRadius: 4, padding: '2px 7px', fontSize: 10, cursor: 'pointer', fontFamily: 'inherit' }}>
                                    ↗ Full
                                  </button>
                                )}
                              </div>
                            : <span style={{ color: '#374151', fontSize: 12 }}>–</span>}
                          {/* Show admin review note if flagged */}
                          {s.review_status === 'flagged' && s.review_note && (
                            <div style={{ background: '#2d0f0f', border: '1px solid #f8717140', borderRadius: 5, padding: '4px 8px', marginTop: 4, color: '#fca5a5', fontSize: 11 }}>
                              Note: {s.review_note}
                            </div>
                          )}
                        </td>
                        {/* SLIP */}
                        <td style={{ padding: '12px 16px' }}>
                          {s.slip_url
                            ? <button onClick={() => setPreviewSlip(s.slip_url)} style={{ background: 'transparent', border: 'none', color: '#f59e0b', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>View</button>
                            : <span style={{ color: '#374151', fontSize: 12 }}>–</span>}
                        </td>
                        {/* SUBMITTED AT */}
                        <td style={{ padding: '12px 16px', color: '#4b5563', fontSize: 11, whiteSpace: 'nowrap' }}>
                          {s.created_at ? new Date(s.created_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : '–'}
                        </td>
                        {/* SEND BACK */}
                        <td style={{ padding: '12px 16px' }}>
                          {s.review_status === 'flagged' ? (
                            <button onClick={() => handleClearFlag(s.id)}
                              style={{ background: '#064e3b', color: '#34d399', border: '1px solid #34d39940', borderRadius: 6, padding: '4px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                              ✓ Clear Flag
                            </button>
                          ) : flagSubId === s.id ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 180 }}>
                              <input
                                placeholder="Note to hub (optional)"
                                value={flagNote}
                                onChange={e => setFlagNote(e.target.value)}
                                autoFocus
                                style={{ background: '#0b0e14', color: '#f3f4f6', border: '1px solid #374151', borderRadius: 6, padding: '5px 8px', fontSize: 11, fontFamily: 'inherit', outline: 'none' }}
                              />
                              <div style={{ display: 'flex', gap: 5 }}>
                                <button onClick={() => handleFlagSubmission(s.id)} disabled={flagging}
                                  style={{ flex: 1, background: '#f97316', color: '#0f1117', border: 'none', borderRadius: 5, padding: '5px 8px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                                  {flagging ? '…' : '⚑ Send Back'}
                                </button>
                                <button onClick={() => { setFlagSubId(null); setFlagNote(''); }}
                                  style={{ background: 'transparent', color: '#6b7280', border: '1px solid #374151', borderRadius: 5, padding: '5px 8px', fontSize: 10, cursor: 'pointer', fontFamily: 'inherit' }}>
                                  ✕
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button onClick={() => { setFlagSubId(s.id); setFlagNote(''); }}
                              style={{ background: '#1c0e00', color: '#f97316', border: '1px solid #f9731640', borderRadius: 6, padding: '4px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                              ⚑ Send Back
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )} {/* end managerView === 'history' */}

      {/* ── Flagged Submissions tab ────────────────────────────────────────── */}
      {managerView === 'flagged' && (
        <div style={{ padding: '24px 0' }}>
          {flaggedSubs.length === 0 ? (
            <div style={{ background: '#0f1117', border: '1px solid #1e2433', borderRadius: 12, padding: 48, textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>✅</div>
              <div style={{ color: '#34d399', fontWeight: 700, fontSize: 14, letterSpacing: 1 }}>NO FLAGGED SUBMISSIONS</div>
              <div style={{ color: '#374151', fontSize: 12, marginTop: 6 }}>All clear — no submissions have been sent back for correction.</div>
            </div>
          ) : (
            <div style={{ background: '#0f1117', border: '1px solid #7f1d1d', borderRadius: 12, overflow: 'auto' }}>
              <div style={{ padding: '14px 20px', borderBottom: '1px solid #7f1d1d', display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ color: '#f87171', fontWeight: 700, fontSize: 13, letterSpacing: 1 }}>⚑ SENT BACK FOR CORRECTION</span>
                <span style={{ background: '#7f1d1d', color: '#f87171', borderRadius: 10, padding: '1px 9px', fontSize: 11, fontWeight: 900 }}>{flaggedSubs.length}</span>
                <span style={{ color: '#4b5563', fontSize: 11, marginLeft: 4 }}>Unflag once the hub has resubmitted and you have verified it.</span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#1a0808' }}>
                    {['HUB', 'FOR DATE', 'KIND', 'PAYMENT', 'SUBMITTED', 'SUB DATE', 'REF / UTR', 'REVIEW NOTE', 'SLIP', 'ACTION'].map(h => (
                      <th key={h} style={{ color: h === 'ACTION' ? '#f97316' : '#6b7280', fontSize: 10, letterSpacing: 1, padding: '11px 16px', textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {flaggedSubs.map((s, i) => {
                    const kind = histSubKind(s);
                    const kindColor = kind === 'Loss'   ? { bg: '#1c0e00', text: '#f97316' }
                      : kind === 'Short'  ? { bg: '#2d0f0f', text: '#f87171' }
                      : kind === 'Excess' ? { bg: '#0c1a2e', text: '#3b82f6' }
                      : { bg: '#0c1c14', text: '#34d399' };
                    const tMap = { cms: 'CMS', neft_flipkart: 'NEFT-FK', neft_wheeley: 'NEFT-WH' };
                    const payLabel = kind === 'Loss' ? 'LOSS/UTILIZED' : (tMap[s.type] || s.type?.toUpperCase() || '—');
                    return (
                      <tr key={s.id} style={{ borderTop: '1px solid #7f1d1d40', background: i % 2 === 0 ? '#140808' : '#0f0606' }}>
                        <td style={{ padding: '11px 16px', color: '#f3f4f6', fontWeight: 700, whiteSpace: 'nowrap' }}>{s.hub}</td>
                        <td style={{ padding: '11px 16px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                          {s.txn_date || '—'}
                          {s.is_direct && <div style={{ color: '#6366f1', fontSize: 9, fontWeight: 700 }}>UNTRACKED</div>}
                        </td>
                        <td style={{ padding: '11px 16px' }}>
                          <span style={{ background: kindColor.bg, color: kindColor.text, borderRadius: 5, padding: '2px 8px', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>
                            {kind === 'Loss' ? '💸 LOSS' : kind === 'Short' ? '⚠️ SHORT' : kind === 'Excess' ? '💰 EXCESS' : '✅ FULL'}
                          </span>
                        </td>
                        <td style={{ padding: '11px 16px' }}>
                          <span style={{ background: '#1c1a00', color: '#fbbf24', borderRadius: 5, padding: '2px 8px', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>{payLabel}</span>
                        </td>
                        <td style={{ padding: '11px 16px', color: '#34d399', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmt(Number(s.amount))}</td>
                        <td style={{ padding: '11px 16px', color: '#9ca3af', whiteSpace: 'nowrap' }}>{s.submission_date || '—'}</td>
                        <td style={{ padding: '11px 16px', color: '#6b7280' }}>{s.ref_number || '—'}</td>
                        <td style={{ padding: '11px 16px', maxWidth: 200 }}>
                          {s.review_note
                            ? <span style={{ color: '#f87171', fontSize: 12, fontStyle: 'italic' }}>"{s.review_note}"</span>
                            : <span style={{ color: '#374151', fontSize: 12 }}>—</span>}
                        </td>
                        <td style={{ padding: '11px 16px' }}>
                          {s.slip_url
                            ? <button onClick={() => setPreviewSlip(s.slip_url)}
                                style={{ color: '#f59e0b', fontSize: 11, fontWeight: 700, background: '#1c150080', border: '1px solid #f59e0b40', borderRadius: 5, padding: '3px 8px', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                                📎 Proof
                              </button>
                            : <span style={{ color: '#374151', fontSize: 12 }}>—</span>}
                        </td>
                        <td style={{ padding: '11px 16px' }}>
                          <button onClick={() => handleClearFlag(s.id)}
                            style={{ background: '#0c2218', color: '#34d399', border: '1px solid #34d39940', borderRadius: 6, padding: '5px 12px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', letterSpacing: 1 }}>
                            ✓ Resolved — Unflag
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )} {/* end managerView === 'flagged' */}

      </>)} {/* end section === 'cod' */}

      {/* Slip Preview Modal */}
      <SlipPreviewModal url={previewSlip} onClose={() => setPreviewSlip(null)} />

      {/* Remarks modal */}
      {viewRemarks && (
        <div style={{ position: 'fixed', inset: 0, background: '#000000cc', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setViewRemarks(null)}>
          <div style={{ background: '#1a1f2e', border: '1px solid #2a2f3e', borderRadius: 12, padding: 24, maxWidth: 480, width: '100%', fontFamily: "'DM Mono', monospace" }}>
            <div style={{ color: '#f59e0b', fontSize: 11, letterSpacing: 2, fontWeight: 700, marginBottom: 10 }}>REMARKS</div>
            <div style={{ color: '#d1d5db', fontSize: 13, lineHeight: 1.7 }}>{viewRemarks}</div>
            <button onClick={() => setViewRemarks(null)}
              style={{ marginTop: 16, background: '#0f1117', color: '#9ca3af', border: '1px solid #2a2f3e', borderRadius: 8, padding: '8px 20px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* Excel-style sum bar */}
      {selectedCells.size > 0 && (() => {
        const values = [...selectedCells.values()];
        const total  = values.reduce((a, v) => a + v, 0);
        const avg    = total / values.length;
        return (
          <div style={{
            position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 4000,
            background: '#0d1117', borderTop: '1px solid #2a2f3e',
            display: 'flex', alignItems: 'center',
            fontFamily: "'DM Mono', monospace", padding: '0 20px', height: 40,
          }}>
            <span style={{ color: '#4b5563', fontSize: 10, letterSpacing: 1, marginRight: 20 }}>
              {selectedCells.size} cell{selectedCells.size > 1 ? 's' : ''} selected
            </span>
            <span style={{ color: '#6b7280', fontSize: 11, marginRight: 24 }}>
              Average: <span style={{ color: '#9ca3af', fontWeight: 700 }}>{fmt(Math.round(avg))}</span>
            </span>
            <span style={{ color: '#6b7280', fontSize: 11, marginRight: 24 }}>
              Count: <span style={{ color: '#9ca3af', fontWeight: 700 }}>{values.length}</span>
            </span>
            <span style={{ color: '#6b7280', fontSize: 11, marginRight: 24 }}>
              Sum: <span style={{ color: '#f59e0b', fontSize: 14, fontWeight: 900 }}>{fmt(total)}</span>
            </span>
            <button onClick={() => setSelectedCells(new Map())}
              style={{ marginLeft: 'auto', background: 'transparent', color: '#4b5563', border: '1px solid #2a2f3e', borderRadius: 6, padding: '4px 12px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>
              ✕ Clear selection
            </button>
          </div>
        );
      })()}
    </div>
  );
}
